import { ArrowLeft, BookOpen, MessageCircle, Play, Radio } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";
import type { Favorite } from "../api/types";
import { CommandButton } from "../components/CommandButton";
import { PageShell } from "../components/PageShell";
import {
  DEFAULT_ERROR_RECOVERY_ACTION,
  StatusBlock,
} from "../components/StatusBlock";
import { useAudioCoordinator } from "../stores/audioCoordinator";
import { useNavigationStore } from "../stores/navigationStore";
import styles from "../styles/App.module.css";

export function FavoritesPage() {
  const back = useNavigationStore((state) => state.back);
  const go = useNavigationStore((state) => state.go);
  const play = useAudioCoordinator((state) => state.play);
  const announce = useAudioCoordinator((state) => state.announce);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [radioFavoritePendingId, setRadioFavoritePendingId] = useState<
    string | null
  >(null);
  const radioFavoritePendingRef = useRef<string | null>(null);

  useEffect(() => {
    api
      .favorites()
      .then((result) => {
        setFavorites(result.items);
        setFavoritesLoaded(true);
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error ? loadError.message : "读取收藏失败。",
        );
        setFavoritesLoaded(true);
      });
  }, []);

  async function playFavorite(favorite: Favorite) {
    if (favorite.target_type === "media_episode") {
      void play({
        title: favorite.title,
        episodeId: favorite.target_id,
        contentType: "media_episode",
        grant: () => api.mediaGrant(favorite.target_id),
      });
      return;
    }

    if (favorite.target_type === "family_message") {
      void play({
        title: favorite.title,
        contentType: "family_message",
        contentId: favorite.target_id,
        grant: () => api.messageGrant(favorite.target_id),
      });
    }

    if (favorite.target_type === "radio_station") {
      if (radioFavoritePendingRef.current) {
        setActionMessage("正在准备收藏的电台，请稍等。");
        announce("正在准备收藏的电台，请稍等。");
        return;
      }

      radioFavoritePendingRef.current = favorite.id;
      setRadioFavoritePendingId(favorite.id);
      setActionMessage("正在准备收藏的电台，请稍等。");
      announce("正在准备收藏的电台，请稍等。");
      setError("");
      try {
        const stations = await api.radioStations({ refresh: true });
        const station = stations.items.find(
          (item) => item.id === favorite.target_id,
        );
        if (!station) {
          setError("没有找到这个电台，请返回上一层。");
          setActionMessage("");
          return;
        }

        void play({
          title: station.title,
          streamUrl: station.stream_url,
          fallbackStreamUrl: station.fallback_url,
          contentType: "radio",
          contentId: station.id,
        });
        setActionMessage("");
      } catch (loadError) {
        setError(
          loadError instanceof Error ? loadError.message : "读取电台失败。",
        );
        setActionMessage("");
      } finally {
        radioFavoritePendingRef.current = null;
        setRadioFavoritePendingId(null);
      }
    }
  }

  return (
    <PageShell title="我的收藏" where="我的收藏页">
      {error ? (
        <StatusBlock
          tone="error"
          message={error}
          recoveryAction={DEFAULT_ERROR_RECOVERY_ACTION}
        />
      ) : null}
      {!error && !favoritesLoaded ? (
        <StatusBlock message="正在读取收藏。" />
      ) : null}
      {!error && actionMessage ? <StatusBlock message={actionMessage} /> : null}
      {!error && favoritesLoaded && !favorites.length ? (
        <StatusBlock message="还没有收藏。听戏或留言时可以请家人帮你收藏。" />
      ) : null}
      <div className={styles.commandList}>
        {favorites.map((favorite, index) => {
          if (favorite.target_type === "media_series") {
            return (
              <CommandButton
                key={favorite.id}
                icon={BookOpen}
                title={`进入：${favorite.title}`}
                hint={`第 ${index + 1} 项，共 ${favorites.length} 项，收藏的节目`}
                onClick={() =>
                  go({ name: "series", seriesId: favorite.target_id })
                }
              />
            );
          }

          const isMessage = favorite.target_type === "family_message";
          const isRadio = favorite.target_type === "radio_station";
          return (
            <CommandButton
              key={favorite.id}
              icon={isMessage ? MessageCircle : isRadio ? Radio : Play}
              title={`播放：${favorite.title}`}
              hint={`第 ${index + 1} 项，共 ${favorites.length} 项，${
                isMessage
                  ? "收藏的家人留言"
                  : isRadio
                    ? radioFavoritePendingId === favorite.id
                      ? "正在准备电台，请稍等"
                      : "收藏的电台"
                    : "收藏的分集"
              }`}
              onClick={() => void playFavorite(favorite)}
            />
          );
        })}
        <CommandButton
          icon={ArrowLeft}
          title="收藏列表末尾，返回上一层"
          hint="这是最后一个收藏操作，双击返回上一层"
          onClick={back}
        />
      </div>
    </PageShell>
  );
}
